import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Box, Flex, Text, Container, useMediaQuery } from "@chakra-ui/react"
import { useGetNewsQuery } from "@core/app/api/globalApi"
import { Article } from "@core/app/types/globalApiType"
import { EmployeeLayout } from "@core/components/EmployeeLayout/EmployeeLayout"
import { Layout } from "@core/components/Layout/Layout"
import { IconChevronDownSmall } from "@core/components/v2/Icon"

import Button from "@core/components/v2/Button"
import { OverflowMenu } from "@core/components/v2/OverflowMenu"
import EmptyState from "./components/EmptyState"
import NewsListSkeleton from "./components/NewsListSkeleton"
import NewsList from "./layout/NewsList"
import { groupArticlesByDate } from "./utils"
import { useTranslation } from "react-i18next"
import useIsEmployee from "@core/utils/hooks/useIsEmployee"
import { useAppTheme } from "@core/utils/contexts/ThemeContext/ThemeContext"

const PAGE_SIZE = 10
const YEAR_COUNT = 10
const SHEET_MOTION_MS = 280
const YEAR_QUERY = 'year'

const readYearFromUrl = () => {
    if (typeof window === 'undefined') return null

    const raw = new URLSearchParams(window.location.search).get(YEAR_QUERY)
    if (!raw || !/^\d{4}$/.test(raw)) return null

    return Number(raw)
}

const writeYearToUrl = (nextYear: number | null) => {
    const params = new URLSearchParams(window.location.search)
    if (nextYear === null) params.delete(YEAR_QUERY)
    else params.set(YEAR_QUERY, String(nextYear))

    const search = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`)
}

const MobileYearSheet = ({
    years,
    year,
    onSelect,
    onClose,
}: {
    years: number[]
    year: number | null
    onSelect: (nextYear: number | null) => void
    onClose: () => void
}) => {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const closingRef = useRef(false)
    const options = [{ label: t('13258', 'All time'), value: null as number | null }, ...years.map((option) => ({ label: String(option), value: option }))]

    useEffect(() => {
        const frame = requestAnimationFrame(() => setVisible(true))
        return () => cancelAnimationFrame(frame)
    }, [])

    const close = () => {
        if (closingRef.current) return
        closingRef.current = true
        setVisible(false)
        window.setTimeout(onClose, SHEET_MOTION_MS)
    }

    if (typeof document === 'undefined') return null

    return createPortal(
        <Box
            position={'fixed'}
            inset={0}
            zIndex={10000}
            bg={visible ? 'rgba(12, 12, 13, 0.45)' : 'rgba(12, 12, 13, 0)'}
            transition={`background-color ${SHEET_MOTION_MS}ms ease`}
            onClick={close}
        >
            <Flex
                direction={'column'}
                position={'absolute'}
                left={'16px'}
                right={'16px'}
                bottom={'16px'}
                gap={'8px'}
                transform={visible ? 'translateY(0)' : 'translateY(calc(100% + 32px))'}
                transition={`transform ${SHEET_MOTION_MS}ms ease`}
                onClick={(event) => event.stopPropagation()}
            >
                <Box
                    bg={'#FFFFFF'}
                    borderRadius={'12px'}
                    maxH={'calc(100vh - 120px)'}
                    overflowX={'hidden'}
                    overflowY={'auto'}
                >
                    {options.map((option, index) => (
                        <Flex
                            key={option.label}
                            as={'button'}
                            type={'button'}
                            align={'center'}
                            justify={'space-between'}
                            w={'full'}
                            h={'48px'}
                            px={'16px'}
                            bg={'transparent'}
                            border={'none'}
                            borderBottom={index < options.length - 1 ? '1px solid #EAEAEE' : 'none'}
                            cursor={'pointer'}
                            textAlign={'left'}
                            _active={{ bg: '#F5F5F5' }}
                            onClick={() => {
                                onSelect(option.value)
                                close()
                            }}
                        >
                            <Text color={'#0C0C0D'} fontSize={'14px'} fontWeight={'medium'} lineHeight={'24px'}>
                                {option.label}
                            </Text>
                            {year === option.value ? (
                                <OverflowMenu.Checked style={{ marginLeft: 0, color: '#245AF0' }} />
                            ) : null}
                        </Flex>
                    ))}
                </Box>
                <Flex
                    as={'button'}
                    type={'button'}
                    align={'center'}
                    justify={'center'}
                    w={'full'}
                    h={'52px'}
                    bg={'#FFFFFF'}
                    border={'none'}
                    borderRadius={'12px'}
                    cursor={'pointer'}
                    color={'#0C0C0D'}
                    fontSize={'14px'}
                    fontWeight={'medium'}
                    lineHeight={'24px'}
                    _active={{ bg: '#F5F5F5' }}
                    onClick={close}
                >
                    {t('416', 'Cancel')}
                </Flex>
            </Flex>
        </Box>,
        document.body
    )
}

const NewsPage = () => {
    const { t } = useTranslation()
    const [isMobile] = useMediaQuery('(max-width: 767px)', { ssr: false })
    const [isYearSheetOpen, setIsYearSheetOpen] = useState(false)
    const currentYear = new Date().getFullYear()
    const years = Array.from({ length: YEAR_COUNT }, (_, index) => currentYear - index)
    const [year, setYear] = useState<number | null>(readYearFromUrl)
    const [currentPage, setCurrentPage] = useState(1)
    const [maxPages, setMaxPages] = useState(1)
    const [articles, setArticles] = useState<Article[]>([])
    const scrollRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)
    const pagingLockRef = useRef(false)
    const { isEmployee } = useIsEmployee()
    const { appTheme } = useAppTheme()
    const background = appTheme?.newSettings?.general?.Background

    const { currentData, isLoading, isFetching } = useGetNewsQuery(
        { limit: PAGE_SIZE, page: currentPage, year: year ?? undefined },
        { skip: currentPage > maxPages }
    )

    useEffect(() => {
        if (!currentData?.data) return

        setMaxPages(currentData.data.pagination?.countPages || 1)
        const incoming = currentData.data.articles || []
        setArticles((prev) => {
            if (currentPage === 1) return incoming

            const seen = new Set(prev.map((article) => article.NewsfeedID))
            const unique = incoming.filter((article) => article?.NewsfeedID && !seen.has(article.NewsfeedID))
            return unique.length ? [...prev, ...unique] : prev
        })
    }, [currentData, currentPage])

    useEffect(() => {
        if (!isFetching && !isLoading) pagingLockRef.current = false
    }, [isFetching, isLoading])

    const hasMore = currentPage < maxPages
    const isInitialLoading = articles.length === 0 && (isLoading || isFetching)
    const isLoadingMore = articles.length > 0 && (isLoading || isFetching)

    const loadNextPage = useCallback(() => {
        if (pagingLockRef.current || !hasMore || isLoading || isFetching) return
        pagingLockRef.current = true
        setCurrentPage((page) => page + 1)
    }, [hasMore, isLoading, isFetching])

    useEffect(() => {
        const target = triggerRef.current
        const root = isEmployee ? scrollRef.current : null
        if (!target || !hasMore || (isEmployee && !root)) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadNextPage()
            },
            { root, rootMargin: '0px 0px 300px 0px', threshold: 0 }
        )

        observer.observe(target)
        return () => observer.disconnect()
    }, [hasMore, isEmployee, loadNextPage])

    const articlesByDate = useMemo(() => groupArticlesByDate(articles), [articles])
    const isEmpty = !isInitialLoading && articles.length === 0

    const selectYear = (nextYear: number | null) => {
        if (nextYear === year) return

        writeYearToUrl(nextYear)
        pagingLockRef.current = false
        setYear(nextYear)
        setArticles([])
        setCurrentPage(1)
        setMaxPages(1)
    }

    const page = (
            <Flex ref={scrollRef} flex={1} minW={0} minH={0} direction={'column'} overflowY={isEmployee ? 'auto' : undefined}>
                <Container
                    maxW={{
                        base: 'container.sm',
                        md: 'container.md',
                        xl: 'container.md',
                    }}
                    w={'full'}
                    flex={1}
                    display={'flex'}
                    flexDirection={'column'}
                >
                    <Flex w={'full'} justifyContent={'space-between'} alignItems={'center'} pt={'24px'} pb={'18px'} mb={'16px'}>
                        <Text fontSize={'24px'} fontWeight={'semibold'} color={'#0C0C0D'} lineHeight={'32px'}>{t('5137', 'News')}</Text>
                        {(!isEmpty || year !== null || isYearSheetOpen) &&
                            (isMobile ? (
                                <>
                                    <Button
                                        variant={'secondary'}
                                        size={'medium'}
                                        className={'NewsYearMenuTrigger'}
                                        aria-expanded={isYearSheetOpen}
                                        rightIcon={<IconChevronDownSmall size={16} color={'#0C0C0D'} />}
                                        style={{ fontSize: '13px', fontWeight: '500', lineHeight: '18px' }}
                                        onClick={() => setIsYearSheetOpen(true)}
                                    >
                                        {year ?? t('13258', 'All time')}
                                    </Button>
                                    {isYearSheetOpen ? (
                                        <MobileYearSheet
                                            years={years}
                                            year={year}
                                            onSelect={selectYear}
                                            onClose={() => setIsYearSheetOpen(false)}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <OverflowMenu placement={'bottom-end'}>
                                    <OverflowMenu.Trigger>
                                        <Button
                                            variant={'secondary'}
                                            size={'medium'}
                                            className={'NewsYearMenuTrigger'}
                                            rightIcon={<IconChevronDownSmall size={16} color={'#0C0C0D'} />}
                                            style={{ fontSize: '13px', fontWeight: '500', lineHeight: '18px' }}
                                        >
                                            {year ?? t('13258', 'All time')}
                                        </Button>
                                    </OverflowMenu.Trigger>
                                    <OverflowMenu.Content className={'NewsYearMenu'}>
                                        <OverflowMenu.ListItem onClick={() => selectYear(null)}>
                                            <span className={'NewsYearMenu__check'}>
                                                {year === null ? <OverflowMenu.Checked /> : null}
                                            </span>
                                            <OverflowMenu.ButtonText>All time</OverflowMenu.ButtonText>
                                        </OverflowMenu.ListItem>
                                        {years.map((option) => (
                                            <OverflowMenu.ListItem key={option} onClick={() => selectYear(option)}>
                                                <span className={'NewsYearMenu__check'}>
                                                    {year === option ? <OverflowMenu.Checked /> : null}
                                                </span>
                                                <OverflowMenu.ButtonText>{option}</OverflowMenu.ButtonText>
                                            </OverflowMenu.ListItem>
                                        ))}
                                    </OverflowMenu.Content>
                                </OverflowMenu>
                            ))}
                    </Flex>
                    {isInitialLoading ? <NewsListSkeleton /> : null}
                    {!isInitialLoading && !isEmpty ? <NewsList articlesByDate={articlesByDate} /> : null}
                    {isLoadingMore ? <NewsListSkeleton count={2} showDate={false} /> : null}
                    {hasMore ? <Box ref={triggerRef} h={'1px'} /> : null}
                    {isEmpty ? (
                        <Flex flex={1} align={'center'} justify={'center'} pb={'48px'}>
                            <EmptyState />
                        </Flex>
                    ) : null}
                </Container>
            </Flex>
    )

    if (isEmployee) {
        return (
            <EmployeeLayout showSidebar showTermsAndPoliciesModal={false} showFooter={false} useContainer={false} lockViewport bgColor={'#FFF'}>
                {page}
            </EmployeeLayout>
        )
    }

    return (
        <Layout useContainer={false} useMobileHeader showFooter bgColor={background ? `#${background}` : undefined}>
            {page}
        </Layout>
    )
}

export default NewsPage